import dagre from "@dagrejs/dagre";
import type { Edge, Node } from "@xyflow/react";
import { MarkerType, Position } from "@xyflow/react";

import { clusterColor, typeColor, EMPHASIS_INK } from "./glyphs";
import type {
  CardNodeData,
  EntityGraphEdge,
  EntityGraphFlow,
  EntityGraphNode,
  LabeledEdgeData,
  LayoutMode,
} from "./types";

export const CARD_W = 226;
export const CARD_H = 150;

/** clusterGrid spacing. */
const COL_GAP = 40;
const ROW_GAP = 34;
const CLUSTER_GAP = 120;
const NO_CLUSTER = "—";
/** Target footprint aspect (w/h) for the cluster meta-grid — midpoint of the
 *  [1.2, 1.6] window so the map is square-ish, neither a strip nor a tower. */
const META_TARGET_ASPECT = 1.4;

/** Distinct cluster keys in first-appearance order (deterministic). */
export function clusterOrder(nodes: EntityGraphNode[]): string[] {
  const order: string[] = [];
  for (const n of nodes) {
    if (n.cluster && !order.includes(n.cluster)) order.push(n.cluster);
  }
  return order;
}

/** Resolve the accent color for a node (explicit → cluster → type). */
export function accentFor(node: EntityGraphNode, order: string[]): string {
  return node.accent ?? clusterColor(node.cluster, order) ?? typeColor(node.type);
}

/** Top-left position per node id. */
type PosMap = Map<string, { x: number; y: number }>;

/**
 * clusterGrid: group nodes by `cluster`, grid each cluster internally (capped
 * rows so a big cluster wraps into several columns instead of one tall stack),
 * then place the cluster BLOCKS into a 2D meta-grid whose column count is chosen
 * to hit a near-square footprint aspect (~`META_TARGET_ASPECT`). This ports the
 * reference's `layout()` cluster-box wrapping (architecture-map.html, line 467)
 * and self-tunes `metaCols` to the card count — fixing the "long horizontal row"
 * the old single-row placement produced (feedback #8a). Robust to sparse edges.
 */
function clusterGridPositions(
  nodes: EntityGraphNode[],
  order: string[],
): PosMap {
  const buckets = new Map<string, EntityGraphNode[]>();
  const ord: string[] = order.slice();
  for (const n of nodes) {
    const k = n.cluster ?? NO_CLUSTER;
    if (!ord.includes(k)) ord.push(k);
    const b = buckets.get(k);
    if (b) b.push(n);
    else buckets.set(k, [n]);
  }

  const rowH = CARD_H + ROW_GAP;
  const colW = CARD_W + COL_GAP;

  // Ordered cluster groups (bucket order).
  const groups: EntityGraphNode[][] = [];
  for (const k of ord) {
    const g = buckets.get(k);
    if (g && g.length) groups.push(g);
  }
  const B = groups.length;
  const pos: PosMap = new Map();
  if (B === 0) return pos;

  type Block = {
    nodes: EntityGraphNode[];
    rows: number;
    cols: number;
    w: number;
    h: number;
  };

  // Build the block set for a given per-cluster row cap.
  const buildBlocks = (rowsCap: number): Block[] =>
    groups.map((group) => {
      const count = group.length;
      const rows = Math.min(count, rowsCap);
      const cols = Math.ceil(count / rows);
      return {
        nodes: group,
        rows,
        cols,
        w: cols * CARD_W + (cols - 1) * COL_GAP,
        h: rows * CARD_H + (rows - 1) * ROW_GAP,
      };
    });

  // Estimate the footprint aspect for a (rowsCap, metaCols) candidate from the
  // meta-grid of block boxes (max block height per meta-row, summed widths).
  const estimateAspect = (blocks: Block[], metaCols: number): number => {
    let cursorX = 0;
    let totalW = 0;
    let rowMaxH = 0;
    let totalH = 0;
    blocks.forEach((b, idx) => {
      if (idx > 0 && idx % metaCols === 0) {
        totalH += rowMaxH + CLUSTER_GAP;
        totalW = Math.max(totalW, cursorX - CLUSTER_GAP);
        cursorX = 0;
        rowMaxH = 0;
      }
      cursorX += b.w + CLUSTER_GAP;
      rowMaxH = Math.max(rowMaxH, b.h);
    });
    totalH += rowMaxH;
    totalW = Math.max(totalW, cursorX - CLUSTER_GAP);
    return totalH > 0 ? totalW / totalH : 1;
  };

  // Jointly search the per-cluster row cap AND the meta-grid column count for
  // the footprint aspect closest to META_TARGET_ASPECT. rowsCap is the dominant
  // lever (it sets how tall the largest cluster block grows); metaCols wraps the
  // blocks. This keeps a 29-node cluster from becoming a tower (feedback #8a).
  const N = nodes.length;
  let best: { rowsCap: number; metaCols: number; dist: number } | null = null;
  for (let rowsCap = 2; rowsCap <= Math.max(2, N); rowsCap++) {
    const blocks = buildBlocks(rowsCap);
    for (let c = 1; c <= B; c++) {
      const aspect = estimateAspect(blocks, c);
      const dist = Math.abs(aspect - META_TARGET_ASPECT);
      if (best === null || dist < best.dist) {
        best = { rowsCap, metaCols: c, dist };
      }
    }
  }
  const chosen = best ?? { rowsCap: Math.max(3, Math.round(Math.sqrt(N))), metaCols: B };
  const blocks = buildBlocks(chosen.rowsCap);
  const metaCols = Math.max(1, Math.min(chosen.metaCols, B));

  // Place blocks into the meta-grid, wrapping after `metaCols` (reference's
  // rowY / xCursor / rowMaxH). Vertically center short blocks within a row.
  type Placed = { block: Block; x: number; y: number };
  const placed: Placed[] = [];
  let cursorX = 0;
  let metaRowY = 0;
  let metaRowMaxH = 0;
  let rowStart = 0; // index into `placed` where the current meta-row began
  const centerRow = (endExclusive: number, rowMaxH: number) => {
    for (let i = rowStart; i < endExclusive; i++) {
      const p = placed[i];
      if (p) p.y += (rowMaxH - p.block.h) / 2;
    }
  };
  blocks.forEach((block, idx) => {
    if (idx > 0 && idx % metaCols === 0) {
      centerRow(placed.length, metaRowMaxH);
      metaRowY += metaRowMaxH + CLUSTER_GAP;
      cursorX = 0;
      metaRowMaxH = 0;
      rowStart = placed.length;
    }
    placed.push({ block, x: cursorX, y: metaRowY });
    cursorX += block.w + CLUSTER_GAP;
    metaRowMaxH = Math.max(metaRowMaxH, block.h);
  });
  centerRow(placed.length, metaRowMaxH); // last meta-row

  // Absolute node positions = block origin + intra-block grid offset.
  for (const { block, x, y } of placed) {
    block.nodes.forEach((n, i) => {
      const col = Math.floor(i / block.rows);
      const row = i % block.rows;
      pos.set(n.id, {
        x: x + col * colW,
        y: y + row * rowH,
      });
    });
  }
  return pos;
}

/**
 * Dagre rank-based layout following the edges (cross-axis cluster band offset
 * kept for a light 2D spread). Use for genuine sequential flows.
 */
function layeredPositions(
  nodes: EntityGraphNode[],
  edges: EntityGraphEdge[],
  order: string[],
  direction: "LR" | "TB",
  present: Set<string>,
): PosMap {
  const BAND_GAP = 96;
  const g = new dagre.graphlib.Graph();
  g.setGraph({ rankdir: direction, nodesep: 30, ranksep: 96, marginx: 24, marginy: 24 });
  g.setDefaultEdgeLabel(() => ({}));

  for (const n of nodes) g.setNode(n.id, { width: CARD_W, height: CARD_H });
  for (const e of edges) {
    if (present.has(e.from) && present.has(e.to)) g.setEdge(e.from, e.to);
  }
  dagre.layout(g);

  const bandOffset = (cluster: string | undefined): number => {
    if (!cluster) return 0;
    const idx = order.indexOf(cluster);
    return idx < 0 ? 0 : idx * BAND_GAP;
  };

  const pos: PosMap = new Map();
  for (const n of nodes) {
    const p = g.node(n.id);
    const off = bandOffset(n.cluster);
    pos.set(n.id, {
      x: (p?.x ?? 0) - CARD_W / 2 + (direction === "TB" ? off : 0),
      y: (p?.y ?? 0) - CARD_H / 2 + (direction === "LR" ? off : 0),
    });
  }
  return pos;
}

/**
 * Lay out the data-driven entity graph (React Flow port of
 * `docs/architecture-map.html`). `mode` selects the positioning strategy;
 * dangling edges (an endpoint not in `nodes`) are dropped like the reference's
 * `if(!f||!t)return`.
 */
export function layoutGraph(
  nodes: EntityGraphNode[],
  edges: EntityGraphEdge[],
  mode: LayoutMode,
  direction: "LR" | "TB",
  flows: EntityGraphFlow[] | undefined,
  activeFlow: string,
): { rfNodes: Node<CardNodeData>[]; rfEdges: Edge<LabeledEdgeData>[] } {
  const order = clusterOrder(nodes);
  const present = new Set(nodes.map((n) => n.id));

  const positions =
    mode === "layered"
      ? layeredPositions(nodes, edges, order, direction, present)
      : clusterGridPositions(nodes, order);

  // Edge endpoints: for clusterGrid, bands run left→right so right→left handles
  // read naturally; for layered, follow the rankdir.
  const sourcePos =
    mode === "layered" && direction === "TB" ? Position.Bottom : Position.Right;
  const targetPos =
    mode === "layered" && direction === "TB" ? Position.Top : Position.Left;

  // Which nodes are touched by the active flow (for dimming).
  const flowNodes = new Set<string>();
  if (activeFlow !== "all") {
    for (const e of edges) {
      if (e.flow === activeFlow && present.has(e.from) && present.has(e.to)) {
        flowNodes.add(e.from);
        flowNodes.add(e.to);
      }
    }
  }

  const rfNodes: Node<CardNodeData>[] = nodes.map((n) => {
    const p = positions.get(n.id) ?? { x: 0, y: 0 };
    const dimmed = activeFlow !== "all" && !flowNodes.has(n.id);
    return {
      id: n.id,
      type: "entity",
      position: { x: p.x, y: p.y },
      sourcePosition: sourcePos,
      targetPosition: targetPos,
      draggable: true,
      data: {
        entity: n,
        accent: accentFor(n, order),
        dimmed,
      },
    };
  });

  const flowColor = (id?: string): string | undefined =>
    flows?.find((f) => f.id === id)?.color;

  const mutedStroke = "hsl(var(--border))";

  // Running 1-based counter over the active (in-flow) edges, in dataset order →
  // drives the numbered circle badge ("5/6/7…" along the selected flow).
  let activeSeq = 0;

  const rfEdges: Edge<LabeledEdgeData>[] = edges
    .filter((e) => present.has(e.from) && present.has(e.to))
    .map((e) => {
      const inFlow = activeFlow === "all" || e.flow === activeFlow;
      const active = activeFlow !== "all" && e.flow === activeFlow;
      const dimmed = activeFlow !== "all" && !inFlow;
      // Base map: muted grey. Active/emphasis: explicit relation color, else the
      // flow color, else near-black EMPHASIS_INK (BLACK accent — feedback #1).
      // Marker defs can't read CSS vars, so a literal is used for both stroke
      // and arrowhead to keep them identical.
      const color = active
        ? e.color ?? flowColor(e.flow) ?? EMPHASIS_INK
        : e.color ?? flowColor(e.flow) ?? mutedStroke;
      const seq = active ? ++activeSeq : undefined;
      return {
        id: e.id,
        source: e.from,
        target: e.to,
        type: "labeled",
        markerEnd: { type: MarkerType.ArrowClosed, color, width: 14, height: 14 },
        data: {
          color,
          dashed: !!e.dashed,
          active,
          dimmed,
          label: e.label,
          seq,
        },
      };
    });

  return { rfNodes, rfEdges };
}
